import type { RGBAColor } from '../map/deckgl-types';
import React from 'react';
import { Link, Typography } from '@mui/material';
import { GeoJsonLayer } from '@deck.gl/layers';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import type { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import { fetchFlatGeobuf } from '../services/featuresApi';
import { loadable } from '../utils/loadable';
import { Legend } from '../components/Legend';
import type { TingOutage } from './types';

export const TING_LAYER_ID = 'ting-outages-layer';

// Bumped by the status line's Retry action; reading it here makes a bump
// re-evaluate the fetch.
const tingOutagesReloadAtom = atom(0);

const EMPTY_COLLECTION: FeatureCollection<Polygon | MultiPolygon, TingOutage> = {
  type: 'FeatureCollection',
  features: [],
};

/**
 * Live Ting outages.
 *
 * `enabled` is the caller's statement that it will use the data. The layer hook
 * is mounted by the shared Map component on every route, including incident
 * views where the Ting layer is never drawn, so an unconditional read pulled
 * the whole outage table on every incident view. Consumers that only exist
 * where Ting is displayed (the sidebar list, the status line) pass `true`.
 */
export const tingOutagesSelector = atomFamily((enabled: boolean) =>
  atom(async get => {
    get(tingOutagesReloadAtom);

    if (!enabled) return EMPTY_COLLECTION;

    // Ordered by the query, not in the browser: TingOutageList renders the rows
    // in exactly this order (features.idx_ting_outages_structures_exposed).
    return fetchFlatGeobuf<Polygon | MultiPolygon, TingOutage>('ting_outages', {
      orderBy: 'structures_exposed',
      orderDirection: 'desc',
    });
  })
);

// Both the live layer (TING_LAYER_ID) and the historical layer
// (weather-ting-events-layer-threats, built by useWeatherEventsLayer) carry
// Ting columns, and both popups key off the layer id.
export const isTingLayerId = (layerId: string): boolean =>
  layerId.includes('ting-outages') || layerId.includes('weather-ting-events');

// Sequential ramp over the vendor's recovery estimate: a fully unrecovered
// outage renders deep red and fades toward green as recovery approaches 100%.
const ZERO_RECOVERY_COLOR: RGBAColor = [214, 40, 40, 255];
const FULL_RECOVERY_COLOR: RGBAColor = [82, 183, 136, 255];
export const TING_FILL_ALPHA = 120;
export const TING_LINE_ALPHA = 220;

export const recoveryColor = (recoveredPercentage: number | null, alpha: number): RGBAColor => {
  // The vendor reports 0 for a brand-new outage; a null estimate renders the
  // same as 0% recovered.
  const t = Math.min(Math.max(recoveredPercentage ?? 0, 0), 1);
  return [
    Math.round(ZERO_RECOVERY_COLOR[0] + (FULL_RECOVERY_COLOR[0] - ZERO_RECOVERY_COLOR[0]) * t),
    Math.round(ZERO_RECOVERY_COLOR[1] + (FULL_RECOVERY_COLOR[1] - ZERO_RECOVERY_COLOR[1]) * t),
    Math.round(ZERO_RECOVERY_COLOR[2] + (FULL_RECOVERY_COLOR[2] - ZERO_RECOVERY_COLOR[2]) * t),
    alpha,
  ];
};

// Module-level so the layer's useMemo identity survives a re-render while the
// fetch is still in flight. Not frozen: that widens it to readonly, which
// FeatureCollection.features rejects.
const EMPTY_FEATURES: Feature<Polygon | MultiPolygon, TingOutage>[] = [];

export const useTingOutagesLayer = ({
  visible,
  // The sidebar list needs the outages whatever the selected map layer is, so
  // the fetch is not gated on `visible`; it is gated on the view displaying
  // Ting at all (see tingOutagesSelector).
  enabled = true,
}: {
  visible: boolean;
  enabled?: boolean;
}) => {
  const outages = useAtomValue(loadable(tingOutagesSelector(enabled)));
  const features = outages.state === 'hasData' ? outages.data.features : EMPTY_FEATURES;

  return React.useMemo(
    () =>
      new GeoJsonLayer<TingOutage>({
        id: TING_LAYER_ID,
        data: { type: 'FeatureCollection', features },
        visible,
        pickable: true,
        stroked: true,
        filled: true,
        lineWidthMinPixels: 1,
        getFillColor: f => recoveryColor(f.properties.recovered_percentage, TING_FILL_ALPHA),
        getLineColor: f => recoveryColor(f.properties.recovered_percentage, TING_LINE_ALPHA),
      }),
    [features, visible]
  );
};

const statusSx = { color: 'white', display: 'block', pb: 1 };

/**
 * Status line shown while Ting is the selected layer: a fetch in flight, how
 * many outage polygons are on the map, and -- most importantly -- a failed
 * fetch, so an API error never masquerades as zero outages.
 */
export const TingOutagesStatus = React.memo(() => {
  // Rendered only while the Ting layer is the one on the map.
  const outages = useAtomValue(loadable(tingOutagesSelector(true)));
  const reload = useSetAtom(tingOutagesReloadAtom);

  if (outages.state === 'loading') {
    return (
      <Typography variant="caption" sx={statusSx}>
        Loading Ting outages...
      </Typography>
    );
  }

  if (outages.state === 'hasError') {
    return (
      <Typography variant="caption" sx={statusSx}>
        Ting outages failed to load{' '}
        <Link
          component="button"
          variant="caption"
          sx={{ color: 'inherit' }}
          onClick={() => reload(count => count + 1)}
        >
          Retry
        </Link>
      </Typography>
    );
  }

  const { features } = outages.data;
  const noun = features.length === 1 ? 'outage polygon' : 'outage polygons';
  // observed_at is uniform across a poll's rows; surfacing it keeps the
  // session-cached set from reading as live current data.
  const observedAt = features[0]?.properties.observed_at ?? null;
  const asOf = observedAt != null ? ` as of ${new Date(observedAt).toLocaleString()}` : '';

  return (
    <Typography variant="caption" sx={statusSx}>
      {`Showing ${features.length.toLocaleString()} ${noun}${asOf}`}
    </Typography>
  );
});

// The ramp is continuous; five evenly spaced samples read as a gradient in the
// legend's 14px swatch rows while still labelling readable percentages.
const LEGEND_RECOVERY_STOPS = [0, 0.25, 0.5, 0.75, 1] as const;

export const TingLegend = React.memo(() => (
  <Legend
    id={TING_LAYER_ID}
    title="Ting Power Outages"
    items={LEGEND_RECOVERY_STOPS.map(stop => {
      const [r, g, b] = recoveryColor(stop, TING_FILL_ALPHA);
      return { label: `${stop * 100}% restored`, color: `rgb(${r}, ${g}, ${b})` };
    })}
  />
));
